import { useCallback, useMemo, useState } from 'react';

import type { SkillTreeNameInput } from './skill-tree-item';

/**
 * A name typed into a file tree row: Enter or leaving the field commits it, Escape cancels.
 * `onCommit` returns why the name cannot be used, shown under the field, or null.
 */
export function useTreeNameInput({
  label,
  onCancel,
  onCommit,
}: {
  label: string;
  onCancel: () => void;
  onCommit: (value: string) => string | null;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const start = useCallback((initial: string) => {
    setValue(initial);
    setError('');
  }, []);
  const commit = useCallback(() => setError(onCommit(value) ?? ''), [onCommit, value]);
  const change = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setError('');
  }, []);
  const keyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // The tree view would otherwise take the keys for its own navigation.
      event.stopPropagation();
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [commit, onCancel]
  );

  const input = useMemo<SkillTreeNameInput>(
    () => ({ error, label, onBlur: commit, onChange: change, onKeyDown: keyDown, value }),
    [change, commit, error, keyDown, label, value]
  );
  return { input, start, value };
}
